import { InputField } from "components/designV2/inputfield/inputfield";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { CompanyUser, UserRole, UserRoleRequest } from "model/Company";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAlertStore } from "stores/alertStore/alertStore";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { validateEmail } from "web-apps/company/utils/utils";
import {
  CompanyUserFormActions,
  CompanyUserFormFields,
  HiddenRadio,
  RadioControl,
  RadioGroup,
  RadioLabel,
  RadioOption,
} from "./companyUsers.styled";
import { encryptPassword } from "utils/encrypt";

export type Position = {
  id: number;
  positionName: string;
};

type Props = {
  setShowAddUserModal: React.Dispatch<React.SetStateAction<boolean>>;
  currentUser: CompanyUser | null;
  setCurrentUser: React.Dispatch<React.SetStateAction<CompanyUser | null>>;
};

type RoleOption = {
  label: string;
  value: UserRoleRequest;
};

export const CompanyUserForm: React.FC<Props> = ({
  setShowAddUserModal,
  currentUser,
  setCurrentUser,
}) => {
  const { t } = useTranslation();
  const [, , alertDispatch] = useAlertStore();
  const [companyState, companyActions] = useCompanyStore();
  const [firstName, setFirstName] = useState(
    currentUser ? currentUser.firstName : "",
  );
  const [lastName, setLastName] = useState(
    currentUser ? currentUser.lastName : "",
  );
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState<UserRoleRequest>(
    UserRoleRequest.COMPANY_ADMIN,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const hasStampClockModule =
    companyState.companySettings?.find(
      (setting: { key: string; value: string }) =>
        setting.key === "CompanyShowStampclockModule",
    )?.value === "true";

  const getDefaultRole = useCallback(() => {
    if (currentUser) {
      switch (currentUser.role) {
        case UserRole.COMPANY_OWNER:
          setRole(UserRoleRequest.COMPANY_OWNER);
          break;
        case UserRole.COMPANY_ADMIN:
          setRole(UserRoleRequest.COMPANY_ADMIN);
          break;
        case UserRole.COMPANY_TIMESTAMP:
          setRole(UserRoleRequest.COMPANY_TIMESTAMP);
          break;
        default:
          setRole(UserRoleRequest.COMPANY_ADMIN);
      }
    } else {
      setRole(UserRoleRequest.COMPANY_ADMIN);
    }
  }, [currentUser]);

  useEffect(() => {
    getDefaultRole();
  }, [getDefaultRole]);

  useEffect(() => {
    if (email && !validateEmail(email)) {
      setEmailError(true);
    } else {
      setEmailError(false);
    }
  }, [email]);

  const roleOptions: RoleOption[] = [
    {
      label: t("CompanyUsers.CompanyOwner"),
      value: UserRoleRequest.COMPANY_OWNER,
    },
    {
      label: t("CompanyUsers.CompanyAdmin"),
      value: UserRoleRequest.COMPANY_ADMIN,
    },
  ];

  if (hasStampClockModule) {
    roleOptions.push({
      label: t("CompanyUsers.CompanyStampClock"),
      value: UserRoleRequest.COMPANY_TIMESTAMP,
    });
  }

  const validate = (): boolean => {
    if (!currentUser && firstName && lastName && email && !emailError && role) {
      return true;
    } else if (currentUser && firstName && lastName) {
      return true;
    } else {
      return false;
    }
  };

  const handleSubmit = async (): Promise<void> => {
    if (!companyState.company || !validate() || isLoading) {
      return;
    }
    try {

      setIsLoading(true);
  
      if (currentUser && role) {
        companyActions
          .updateCompanyUser(
            companyState.company.id,
            currentUser.id,
            currentUser.firebaseId,
            role,
            firstName,
            lastName,
          )
          .then(() => {
            setShowAddUserModal(false);
            setCurrentUser(null);
            setIsLoading(false);
          })
          .catch(() => {
            alertDispatch({
              type: "SHOW_ALERT",
              payload: {
                icon: "alert",
                title: t("Alerts.OhNo"),
                message: t("CompanyUsers.UserCouldNotBeUpdated"),
              },
            });
            setIsLoading(false);
          });
        return;
      }
  
      if (role) {
        let data = {
          firstName,
          lastName,
          email,
          role,
          provisionToken: ''
        };
        if (role === UserRoleRequest.COMPANY_TIMESTAMP) {
          data.provisionToken = password !== '' ? await encryptPassword(password, email): ''
        }
  
        companyActions
          .addCompanyUser(companyState.company.id, data)
          .then(() => {
            setShowAddUserModal(false);
            setIsLoading(false);
          })
          .catch(() => {
            alertDispatch({
              type: "SHOW_ALERT",
              payload: {
                icon: "alert",
                title: t("Alerts.OhNo"),
                message: t("CompanyUsers.CouldNotAddUser"),
              },
            });
            setIsLoading(false);
          });
      }
    } catch(err) {
      setIsLoading(false);
      alertDispatch({
        type: "SHOW_ALERT",
        payload: {
          icon: "alert",
          title: t("Alerts.OhNo"),
          message: t("CompanyUsers.CouldNotAddUser"),
        },
      });
    }
  };

  const handleCancel = (): void => {
    setShowAddUserModal(false);
    setCurrentUser(null);
  };

  return (
    <CompanyUserFormFields>
      <InputField
        label={t("CompanyUsers.FirstName")}
        value={firstName}
        onChange={setFirstName}
        fullWidth
      />
      <InputField
        label={t("CompanyUsers.LastName")}
        value={lastName}
        onChange={setLastName}
        fullWidth
      />
      {!currentUser && (
        <InputField
          label={t("CompanyUsers.Email")}
          value={email}
          type="email"
          onChange={setEmail}
          hasError={emailError}
          errorText={t("CompanyUsers.InvalidEmail")}
          fullWidth
        />
      )}
      {!currentUser && role == UserRoleRequest.COMPANY_TIMESTAMP && (
        <InputField
          label={t('General.Password')}
          value={password}
          type="password"
          onChange={setPassword}
          fullWidth
        />
      )}

      <RadioGroup role="radiogroup" aria-label={t("CompanyUsers.Role")}>
        {roleOptions.map((option) => (
          <RadioOption key={option.value}>
            <HiddenRadio
              type="radio"
              name="company-user-role"
              value={option.value}
              checked={role === option.value}
              onChange={() => {
                if (option.value !== UserRoleRequest.COMPANY_TIMESTAMP) {
                  setPassword('');
                }
                setRole(option.value);
              }}
            />
            <RadioControl />
            <RadioLabel>{option.label}</RadioLabel>
          </RadioOption>
        ))}
      </RadioGroup>

      <CompanyUserFormActions>
        <SecondaryButton
          label={t("General.Cancel")}
          disabled={isLoading}
          onClick={handleCancel}
        />
        <PrimaryButton
          label={t("CompanyUsers.Save")}
          disabled={!validate()}
          isLoading={isLoading}
          onClick={handleSubmit}
        />
      </CompanyUserFormActions>
    </CompanyUserFormFields>
  );
};
